/**
 * Redirect Path Validation
 *
 * Prevents open redirect attacks by ensuring redirect paths are
 * safe, relative paths that stay on the same origin.
 *
 * Blocks:
 * - Protocol-relative URLs (//evil.com)
 * - javascript:, data:, vbscript: protocols
 * - Absolute URLs with external hosts
 */

/**
 * Validates that a redirect path is safe (relative path only).
 *
 * @param path - The path to validate
 * @param fallback - Fallback path if invalid (default: '/')
 * @returns Safe path or fallback if invalid
 */
export function validateRedirectPath(path: string, fallback = '/'): string {
  if (!path.startsWith('/')) {
    return fallback;
  }

  if (path.startsWith('//')) {
    return fallback;
  }

  if (path.includes('..')) {
    return fallback;
  }

  if (path.includes('\\')) {
    return fallback;
  }

  const lowerPath = path.toLowerCase();
  // eslint-disable-next-line no-script-url -- Security check, not actual usage
  if (lowerPath.includes('javascript:') || lowerPath.includes('data:') || lowerPath.includes('vbscript:')) {
    return fallback;
  }

  try {
    const url = new URL(path, 'http://localhost');
    if (url.host !== 'localhost') {
      return fallback;
    }
    return url.pathname + url.search + url.hash;
  } catch {
    return fallback;
  }
}
